import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { ArrowLeftRight, ClipboardCheck, PackagePlus, Star } from 'lucide-react'
import { getBinContents, getInventoryItems, setPickLocation } from '../../api/inventory'
import ErrorBanner from '../../components/ErrorBanner'
import { useToast } from '../../components/Toast.jsx'
import { num } from '../sales/salesShared'
import BinNav from './BinNav'
import BinScan from './BinScan'
import ItemPicker from './ItemPicker'
import { binLabel, qty, stockItems, useAllAreas, useBins, useItemBins, useLocations } from './inventoryShared'

// Two ways round: which bins hold an item (and its pick bin), or what is in
// one bin (scan its label).
export default function BinStockBySku() {
  const [params, setParams] = useSearchParams()
  const mode = params.get('bin') ? 'bin' : (params.get('mode') === 'bin' ? 'bin' : 'item')
  const setMode = (m) => setParams(m === 'bin' ? { mode: 'bin' } : {}, { replace: true })

  return (
    <div className="ci-page">
      <BinNav title="Where is it?" meta={mode === 'bin' ? 'Scan a bin’s label, or choose it, to see what’s in it.' : 'Choose or scan an item to see which bins hold it.'} />
      <div className="segmented" role="radiogroup" aria-label="Look up">
        <button type="button" role="radio" aria-checked={mode === 'item'} className={mode === 'item' ? 'active' : ''} onClick={() => setMode('item')}>
          Find an item<small>Which bins hold it</small>
        </button>
        <button type="button" role="radio" aria-checked={mode === 'bin'} className={mode === 'bin' ? 'active' : ''} onClick={() => setMode('bin')}>
          Check a bin<small>What’s in it</small>
        </button>
      </div>
      {mode === 'bin' ? <BinLookup params={params} setParams={setParams} /> : <ItemLookup params={params} setParams={setParams} />}
    </div>
  )
}

function ItemLookup({ params, setParams }) {
  const { notify } = useToast()
  const [items, setItems] = useState([])
  const [version, setVersion] = useState(0)
  const [busy, setBusy] = useState(null)
  const [error, setError] = useState('')
  const productId = params.get('product_id') || ''
  const setProductId = (id) => setParams(id ? { product_id: id } : {}, { replace: true })

  useEffect(() => { getInventoryItems().then((d) => setItems(stockItems(d))).catch(() => setItems([])) }, [])

  const item = items.find((p) => String(p.product_id) === String(productId))
  const bins = useItemBins(item?.sku, version)
  const rows = (bins?.rows || []).filter((r) => num(r.qty) !== 0)
  const inBins = rows.reduce((s, r) => s + num(r.qty), 0)
  const loose = Math.max(0, num(item?.qty_on_hand) - inBins)

  const makePick = async (r) => {
    setBusy(r.area_id)
    setError('')
    try {
      await setPickLocation({ product_id: item.product_id, area_id: r.area_id })
      notify(`${item.name || item.sku} is now picked from ${r.path}`)
      setVersion((v) => v + 1)
    } catch (e) {
      setError(e.message || 'Could not set the pick bin.')
    } finally {
      setBusy(null)
    }
  }

  return (
    <>
      {error && <ErrorBanner message={error} />}

      <div className="ci-form-meta-card">
        <ItemPicker items={items} value={productId} onChange={setProductId} />
      </div>

      {item && bins && (
        <section className="doc-card">
          <div className="doc-card-head">
            <h2>{item.name || item.sku}</h2>
            <span>{qty(item.qty_on_hand)} on hand · picked from {bins.pick_path || 'no bin yet'}</span>
          </div>
          {rows.length ? (
            <div className="doc-table-scroll">
              <table className="ci-line-table">
                <thead><tr><th>Location</th><th>Bin</th><th className="num">In the bin</th><th /></tr></thead>
                <tbody>
                  {rows.map((r) => {
                    const isPick = r.path && r.path === bins.pick_path
                    return (
                      <tr key={`${r.location_id}-${r.area_id}`}>
                        <td>{r.location}</td>
                        <td><Link to={`/inventory/sku-bins?bin=${r.area_id}`}>{r.path}</Link>{r.area_name && <span className="cell-sub">{r.area_name}</span>}</td>
                        <td className="num">{qty(r.qty)}</td>
                        <td className="num">
                          {isPick
                            ? <span className="status-badge tone-good"><Star size={11} aria-hidden="true" /> Pick bin</span>
                            : <button type="button" className="ci-btn ci-btn-ghost ci-btn-sm" disabled={busy != null} onClick={() => makePick(r)}>Make pick bin</button>}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          ) : <p className="doc-empty">Not in any bin yet.</p>}
          {loose > 0 && (
            <p className="doc-note inv-card-note">
              {qty(loose)} {loose === 1 ? 'isn’t' : 'aren’t'} in a bin yet. <Link to={`/inventory/bin-move-sku?product_id=${item.product_id}`}>Put {loose === 1 ? 'it' : 'them'} away</Link>
            </p>
          )}
          <div className="inv-card-actions">
            <Link className="ci-btn ci-btn-ghost ci-btn-sm" to={`/inventory/bin-move-sku?mode=move&product_id=${item.product_id}`}><ArrowLeftRight size={13} />Move between bins</Link>
            <Link className="ci-btn ci-btn-ghost ci-btn-sm" to={`/inventory/bin-move-sku?product_id=${item.product_id}`}><PackagePlus size={13} />Put away</Link>
            <Link className="ci-btn ci-btn-ghost ci-btn-sm" to={`/inventory/stock/${item.product_id}`}>Item details</Link>
          </div>
        </section>
      )}
    </>
  )
}

function BinLookup({ params, setParams }) {
  const locations = useLocations()
  const allAreas = useAllAreas()
  const [loc, setLoc] = useState('')
  const [data, setData] = useState(null)
  const [error, setError] = useState('')
  const areaId = params.get('bin') || ''
  const setAreaId = (id) => setParams(id ? { bin: String(id) } : { mode: 'bin' }, { replace: true })
  const bins = useBins(loc)
  const hasBins = allAreas.some((a) => a.level === 'Bin')

  useEffect(() => { if (locations?.length && !loc) setLoc(String(locations[0].location_id)) }, [locations, loc])
  // A bin given in the address (e.g. from a scan) sets its location.
  useEffect(() => {
    const a = allAreas.find((x) => String(x.area_id) === String(areaId))
    if (a && String(a.location_id) !== String(loc)) setLoc(String(a.location_id))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allAreas, areaId])
  useEffect(() => {
    setData(null)
    setError('')
    if (!areaId) return
    getBinContents(areaId).then(setData).catch((e) => setError(e.message || 'Could not open that bin.'))
  }, [areaId])

  const total = (data?.rows || []).reduce((s, r) => s + num(r.qty), 0)

  return (
    <>
      {error && <ErrorBanner message={error} />}
      <div className="ci-form-meta-card">
        <div className="ci-form-meta-top two">
          <div className="ci-form-field">
            <span>Scan the bin</span>
            <BinScan
              areas={allAreas}
              locationId={loc}
              onPick={(a) => { setLoc(String(a.location_id)); setAreaId(a.area_id) }}
              validate={(a) => (hasBins && a.level !== 'Bin' ? `That’s ${a.level === 'Aisle' ? 'an' : 'a'} ${a.level.toLowerCase()} label (${a.path}). Scan a bin.` : '')}
            />
          </div>
          <div className="inv-or-pick">
            {locations?.length > 1 && (
              <label className="ci-form-field">
                <span>Or choose: location</span>
                <select value={loc} onChange={(e) => { setLoc(e.target.value); setAreaId('') }}>
                  {locations.map((l) => <option key={l.location_id} value={l.location_id}>{l.name}</option>)}
                </select>
              </label>
            )}
            <label className="ci-form-field">
              <span>{locations?.length > 1 ? 'Bin' : 'Or choose the bin'}</span>
              <select value={areaId} onChange={(e) => setAreaId(e.target.value)}>
                <option value="">{bins.length ? 'Choose a bin…' : 'No bins here yet'}</option>
                {bins.map((b) => <option key={b.area_id} value={b.area_id}>{binLabel(b)}</option>)}
              </select>
            </label>
          </div>
        </div>
      </div>

      {data && (
        <section className="doc-card">
          <div className="doc-card-head">
            <h2>{data.area.path}{data.area.name ? ` · ${data.area.name}` : ''}</h2>
            <span>{data.area.location_name} · {data.rows.length} {data.rows.length === 1 ? 'item' : 'items'}, {qty(total)} in all</span>
          </div>
          {data.rows.length ? (
            <div className="doc-table-scroll">
              <table className="ci-line-table">
                <thead><tr><th>Item</th><th className="num">In the bin</th><th /></tr></thead>
                <tbody>
                  {data.rows.map((r) => (
                    <tr key={r.product_id}>
                      <td><Link to={`/inventory/sku-bins?product_id=${r.product_id}`}>{r.name || r.sku}</Link><span className="cell-sub">{r.sku}</span></td>
                      <td className="num">{qty(r.qty)}</td>
                      <td className="num">
                        <div className="row-actions">
                          <Link className="ci-btn ci-btn-ghost ci-btn-sm" to={`/inventory/bin-adjust-sku?product_id=${r.product_id}&area_id=${data.area.area_id}`}><ClipboardCheck size={13} />Count</Link>
                          <Link className="ci-btn ci-btn-ghost ci-btn-sm" to={`/inventory/bin-move-sku?mode=move&product_id=${r.product_id}&from_area_id=${data.area.area_id}`}><ArrowLeftRight size={13} />Move</Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <p className="doc-empty">This bin is empty.</p>}
        </section>
      )}
    </>
  )
}
